import { motion } from "framer-motion";
import { ArrowUpRight, Lock, FileSignature, BrainCircuit, ShieldAlert, Activity } from "lucide-react";
import { VENTURES } from "../data/cv";
import { fadeUp, stagger, viewportOnce, onSpotMove } from "../lib/anim";

const ICONS = { eparchi: FileSignature, "nirogyam-ai": BrainCircuit, occusafe: ShieldAlert, labtrack: Activity };
const SPANS = ["lg:col-span-7", "lg:col-span-5", "lg:col-span-5", "lg:col-span-7"];

export default function Ventures() {
  return (
    <section id="ventures" className="relative py-16 sm:py-24 lg:py-32">
      <div className="absolute top-1/4 -left-40 w-[420px] h-[420px] rounded-full bg-vio/10 orb" />
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 relative">
        <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={viewportOnce}>
          <motion.p variants={fadeUp} className="font-mono text-xs tracking-[0.22em] uppercase text-neon mb-4 flex items-center gap-3">
            <span className="w-8 h-px bg-gradient-to-r from-neon to-transparent" /> 02 — Flagship builds
          </motion.p>
          <motion.h2 variants={fadeUp} custom={1} className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight max-w-2xl">
            Four health-tech products. <span className="text-grad">Built for real clinical workflows.</span>
          </motion.h2>
        </motion.div>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-6">
          {VENTURES.map((v, i) => {
            const Icon = ICONS[v.id];
            const Card = (
              <div
                onMouseMove={onSpotMove}
                className="spot-card group relative h-full panel rounded-2xl p-6 sm:p-8 flex flex-col min-h-[280px] transition-transform duration-500 hover:-translate-y-1.5"
              >
                <div className="flex items-start justify-between">
                  <span className="grid place-items-center w-12 h-12 rounded-xl border border-white/10 bg-white/[0.04] text-neon group-hover:text-vio group-hover:border-vio/40 transition-colors duration-500">
                    <Icon size={22} />
                  </span>
                  <span className="font-mono text-xs text-slate-600 tracking-[0.2em]">{v.index}</span>
                </div>

                <div className="mt-7">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h3 className="font-display text-xl sm:text-2xl font-semibold tracking-tight">{v.name}</h3>
                    {!v.live && (
                      <span className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-slate-500 border border-white/10 rounded-full px-2.5 py-1">
                        <Lock size={9} /> In deployment
                      </span>
                    )}
                  </div>
                  <p className="mt-3 text-sm sm:text-base text-slate-400 leading-relaxed">{v.tagline}</p>
                  <p className="mt-2 text-sm text-slate-500 leading-relaxed hidden sm:block">{v.detail}</p>
                </div>

                <div className="mt-auto pt-7 flex items-end justify-between gap-4">
                  <div className="flex flex-wrap gap-2">
                    {v.tech.map((t) => (
                      <span key={t} className="text-[11px] text-slate-400 border border-white/10 rounded-full px-2.5 py-1 bg-white/[0.03]">
                        {t}
                      </span>
                    ))}
                  </div>
                  <span className={`flex items-center gap-1.5 text-xs font-mono shrink-0 ${v.url ? "text-neon group-hover:text-white transition-colors duration-300" : "text-slate-600"}`}>
                    {v.url ? <>Visit <ArrowUpRight size={13} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" /></> : v.role}
                  </span>
                </div>
                <span className="pointer-events-none absolute top-0 left-6 right-6 h-px bg-gradient-to-r from-transparent via-neon/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </div>
            );

            return (
              <motion.div
                key={v.id}
                variants={fadeUp}
                custom={i}
                initial="hidden"
                whileInView="show"
                viewport={viewportOnce}
                className={SPANS[i]}
                data-testid={`venture-${v.id}-card`}
              >
                {v.url ? (
                  <a href={v.url} target="_blank" rel="noopener noreferrer" className="block h-full" data-testid={`venture-${v.id}-link`}>
                    {Card}
                  </a>
                ) : (
                  Card
                )}
              </motion.div>
            );
          })}
        </div>

        <motion.p variants={fadeUp} initial="hidden" whileInView="show" viewport={viewportOnce} className="mt-8 text-sm text-slate-500 font-mono">
          Roles: creator &amp; architect of e-parchi · chief innovator on Nirogyam AI · systems architect on Occusafe · co-creator &amp; hematology domain expert on LabTrack (Pathlete India).
        </motion.p>
      </div>
    </section>
  );
}